import React, { useMemo, useState } from 'react';
import { Button, Input, Modal, Select, Space, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetVwConstMachines } from '../../hooks/genericHooks';
import type { VwConstMachines } from '../../types';

type Props = {
  open: boolean;
  onCancel: () => void;
  onOk: (payload: { machine_id: number; label: string; row: VwConstMachines }) => void;
  title?: string;
};

const includesText = (value: unknown, query: string) =>
  String(value ?? '').toLowerCase().includes(query.toLowerCase());

const buildMachineLabel = (m: VwConstMachines): string => {
  const type = String((m as any)?.machine_type ?? '').trim();
  const plate = String((m as any)?.license_plate ?? '').trim();
  const make = String((m as any)?.make ?? '').trim();
  const model = String((m as any)?.model ?? '').trim();
  const base = [type || null, plate || null].filter(Boolean).join(' - ');
  const mm = `${make} ${model}`.trim();
  return mm ? `${base || `ID ${m.id}`} (${mm})` : (base || `ID ${m.id}`);
};

const MachineSelect: React.FC<Props> = ({ open, onCancel, onOk, title }) => {
  const { data: machinesViewData, isLoading } = useGetVwConstMachines();

  const machines: VwConstMachines[] = Array.isArray(machinesViewData)
    ? (machinesViewData as any)
    : ((machinesViewData as any)?.data || []);

  const [q, setQ] = useState('');
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const companyOptions = useMemo(() => {
    const uniq = new Set<string>();
    (machines || []).forEach((m: any) => {
      const v = m?.company_name != null ? String(m.company_name).trim() : '';
      if (v) uniq.add(v);
    });
    return Array.from(uniq.values())
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ value: v, label: v }));
  }, [machines]);

  const filtered = useMemo(() => {
    const tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
    return (machines || []).filter((m: any) => {
      if (companyName && String((m as any)?.company_name ?? '').trim() !== companyName) return false;
      if (!tokens.length) return true;

      return tokens.every((t) => (
        includesText(m?.id, t) ||
        includesText(m?.machine_type, t) ||
        includesText(m?.license_plate, t) ||
        includesText(m?.make, t) ||
        includesText(m?.model, t)
      ));
    });
  }, [companyName, machines, q]);

  React.useEffect(() => {
    // When filter changes, reset to first page so table doesn't jump to the last available page.
    setCurrentPage(1);
  }, [q, companyName]);

  const selectedRow: VwConstMachines | null = useMemo(() => {
    if (selectedId == null) return null;
    return (machines || []).find((m: any) => Number(m?.id) === Number(selectedId)) ?? null;
  }, [machines, selectedId]);

  const columns: ColumnsType<VwConstMachines> = useMemo(() => {
    return [
      {
        title: 'Label',
        key: 'label',
        width: 260,
        ellipsis: true,
        render: (_: any, record: VwConstMachines) => buildMachineLabel(record),
      },
      { title: 'Type', dataIndex: 'machine_type', key: 'machine_type', width: 120, ellipsis: true },
      { title: 'Make', dataIndex: 'make', key: 'make', width: 110, ellipsis: true },
      { title: 'Model', dataIndex: 'model', key: 'model', width: 110, ellipsis: true },
    ];
  }, []);

  return (
    <Modal
      open={open}
      title={title || 'Select Machine'}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Space style={{ width: '100%' }} size={10} align="start">
          <Input
            placeholder="Search by type, plate, make, model..."
            allowClear
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ flex: 1, minWidth: 300 }}
          />
          <Select
            placeholder="Company"
            allowClear
            value={companyName ?? undefined}
            onChange={(v) => setCompanyName(v != null ? String(v) : null)}
            options={companyOptions}
            style={{ width: 150, flex: '0 0 auto' }}
            showSearch
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
          <Space size={8} style={{ flex: '0 0 auto' }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              disabled={selectedRow == null}
              onClick={() => {
                if (!selectedRow) return;
                onOk({ machine_id: Number((selectedRow as any).id), label: buildMachineLabel(selectedRow), row: selectedRow });
              }}
            >
              OK
            </Button>
          </Space>
        </Space>

        <Table<VwConstMachines>
          size="small"
          rowKey={(r) => String((r as any).id)}
          loading={isLoading}
          dataSource={filtered}
          columns={columns}
          scroll={{ x: 650 }}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page) => setCurrentPage(page),
            onShowSizeChange: (_current, size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
          onRow={(record) => ({
            onClick: () => {
              const id = Number((record as any).id);
              if (!Number.isFinite(id)) return;
              setSelectedId(id);
            },
            onDoubleClick: () => {
              const id = Number((record as any).id);
              if (!Number.isFinite(id)) return;
              onOk({ machine_id: id, label: buildMachineLabel(record), row: record });
            },
            style: {
              cursor: 'pointer',
              background: selectedId != null && Number((record as any).id) === selectedId ? '#e6f7ff' : undefined,
            },
          })}
        />
      </Space>
    </Modal>
  );
};

export default MachineSelect;
